/**
 * Frozen opaque ids for the bundled measurement vocabulary (ADR-0007 §2/§5).
 *
 * Units and dimensions are opaque-id definitions (`dim_…` / `unit_…`) on par
 * with predicates / traits / calendars. `mintVocabId` uses `randomUUID`
 * (non-deterministic), so the ids for the engine-bundled common dimensions
 * and units are frozen here and COMMITTED as static data — identity must be
 * stable across every build, otherwise a campaign that stored a canonical
 * value tagged with a unit id would lose its provenance unit on the next
 * deploy. DO NOT regenerate; edit by hand only if a token must be re-mapped.
 *
 * Maps the former readable dotted token (e.g. `metric.km`, `currency.gp`) to
 * its opaque minted id. The token survives only as a stable lookup key for
 * engine code + tests (display is the unit's `name`/`symbol`, which are
 * freely duplicable — the id is identity). This mirrors `official-core-ids.ts`.
 */

/** Former-token → opaque `dim_` id for every bundled dimension. */
export const COMMON_DIMENSION_IDS = {
  length: 'dim_277ceda1-fd3c-47e2-99ec-de49cc82cde0',
  mass: 'dim_bfd55455-90cc-46c0-a23f-a400c93b0e29',
  temperature: 'dim_e432f5a7-ec18-4cfc-abc6-52ee13af68b3',
  time: 'dim_bb20a84f-05c8-482a-99bc-8c81186c73ec',
  currency: 'dim_1b16842b-ca0f-4101-9ea8-d9956c99675b',
} as const satisfies Record<string, string>;

/** Former-token → opaque `unit_` id for every bundled unit. */
export const COMMON_UNIT_IDS = {
  'metric.mm': 'unit_295b5bf9-c4a9-4b19-a3bc-eb714e630dce',
  'metric.cm': 'unit_d365c991-908f-4185-b146-ab6acc6089f0',
  'metric.m': 'unit_511cb08b-216b-4064-bdcc-e891dd010764',
  'metric.km': 'unit_294e1701-b658-4f25-9a6d-06faf3bc8a1c',
  'imperial.in': 'unit_876faf0d-3401-41e4-bda0-22b6bf1973ed',
  'imperial.ft': 'unit_a4f40d12-51bb-480b-a5ef-ad509aad0bd7',
  'imperial.yd': 'unit_40ab5705-482e-42f3-9eb0-b805ea85f97c',
  'imperial.mi': 'unit_9c598277-eb6a-4678-82e2-de45b791788f',
  'metric.g': 'unit_9830db24-77fb-40a4-8d49-a71f84d7217e',
  'metric.kg': 'unit_91e7eba8-dc8b-4f12-8c9a-4f6d5e1199aa',
  'metric.t': 'unit_d226e0ab-afba-43ac-bde4-48f26bd14fc2',
  'imperial.oz': 'unit_7a854ca3-2460-4f64-b95e-b72cf9bdd2e3',
  'imperial.lb': 'unit_7d0ced35-84b9-4f21-b3ca-4990e5d2c180',
  'metric.celsius': 'unit_4fb312e6-b27e-4777-94c5-255ecf79126c',
  'imperial.fahrenheit': 'unit_8f4540d6-431b-4645-8597-2773c7442cbf',
  'time.second': 'unit_d1ec415a-8e0f-4a1d-accb-63c2c2a8ec7d',
  'time.minute': 'unit_444a3fba-b227-4b16-b26f-545dbd783680',
  'time.hour': 'unit_367588ea-a56c-4f0c-97e4-052584b0799f',
  'time.day': 'unit_b6063b48-28b8-44ff-9067-538aa21ca9df',
  'currency.cp': 'unit_1eb24619-df6c-4407-b3e0-9cc8e4e02e08',
  'currency.sp': 'unit_f9267acd-df90-4e48-bd3d-68008e26fe0c',
  'currency.ep': 'unit_d7d9c0d8-58ab-4eef-b733-776529a2ec7e',
  'currency.gp': 'unit_e519794b-5dab-4de5-9ff1-1cc068359c32',
  'currency.pp': 'unit_6dcc93c2-f7c3-44cd-aaa9-93b7339143db',
} as const satisfies Record<string, string>;

export type CommonDimensionToken = keyof typeof COMMON_DIMENSION_IDS;
export type CommonUnitToken = keyof typeof COMMON_UNIT_IDS;
